<template>
  <GcBaseLayout
    :title="title"
    :isXSmall="isXSmall"
    :isLarge="isLarge"
    :baseView="baseView"
    :key="pageKey"
    :backButtonEnable="!!backButtonRoute"
    :backButtonFunction="backButtonFunction"
    :formsConfig="formsConfig"
    :tabs="getTabs"
    :selectedIndex="selectedTabIndex"
    ref="gc-base-layout"
    class="bill-of-materials"
  >
    <slot></slot>

    <template #headerContent>
      <GcHeaderContentDynamic
        :title="'Bill of Materials'"
        :createModeIsEditMode="true"
        :baseView="baseView"
        :primaryLabel="
          baseView.mode === 'create'
            ? 'New Bill of Materials'
            : 'Bill of Materials # ' + baseView.id
        "
        :secondaryLabel="undefined"
        v-if="loaded"
      />
    </template>

    <div class="css-v2 transaction-screen">
      <gc-header-form
        :headerOptions="getHeaderScreenJson"
        :data="loaded ? model : {}"
        :saveTransaction="saveTransaction"
        :backButtonRoute="backButtonRoute"
        ref="gc-header-form-component"
        :getHeaderToolbar="undefined"
        :setLoading="showLoading"
      >
      </gc-header-form>
    </div>

    <dx-tooltip :ref="TooltipRefName" position="top">
      <div class="tooltipContent">
        <div>
          This is the default BOM for the selected product. Product and Bill
          Type cannot be changed.
        </div>
      </div>
    </dx-tooltip>

    <GCCopyBOM
      :visible="getCopyBOMVisible"
      @setCopyBOMVisible="setCopyBOMVisible"
      :popUpConfig="popUpConfig"
      :displayServerError="displayServerError"
      @reloadBOMDetails="loadBOMDetails"
      v-if="getCopyBOMVisible"
    />
  </GcBaseLayout>
</template>

<script>
import DataGridMixin from "@/_mixins/data-grid.mixin";
import FormMixin from "@/_mixins/form.mixin";
import RouteMixin from "@/_mixins/route.mixin";

import {
  BillOfMaterialsTabs,
  GetIndexByTabName,
} from "../../../_models/tabdata/production/bill-of-materials-tabs";
import { getFactorByCode } from "../../../_store/helpers";
import { backwardTabbing, forwardTabbing } from "../../../_helpers/ui/keyboard";
import { BillOfMaterialsService } from "../../../_services/api/production/bill-of-materials.service";
import { IsResponseDataValid } from "../../../_helpers/ui/helpers";
import { ProductService } from "../../../_services/api/products/product.service";
import { DefaultCompanyBranchDivisionService } from "../../../_services/api/sales/default-company-branch-dicision.service";
import { FeatureStorage } from "../../../_services/storage";
import GCCopyBOM from "../../../popups/bill-of-materials/gc-popup-copy-bom.vue";

import { on } from "devextreme/events";

export default {
  mixins: [RouteMixin, FormMixin, DataGridMixin],
  components: {
    GCCopyBOM,
  },
  data() {
    return {
      loaded: false,
      model: {
        BillNo: null,
        BomTypeCode: "Production",
        IsActive: true,
        Description1: null,
        Description2: null,
        BranchCode: null,
        DivisionCode: null,
        PackSize: 1,
        UnitOfMeasureCode: null,
        BatchSize: 1,
        Margin: 0,
        WasteReleaseStrategy: "None",
        BatchExpiryMethod: "None",
        IsBatchProduct: "No",
        WorkOrderPerBatch: false,
        WipLeadDays: 0,
        WorkOrderPerBatchRoundingValue: "None",
        WorkOrderPerBatchRoundingDirection: "Up",
        SchedulingMethod: "Continuous Flow",
        Comment: null,
        PrimaryBomCode: null,
        PlanningBomCode: null,
        DateChanged: null,
        DateCreated: null,
        CreatedBy: null,
        ChangedBy: null,
        CompanyCode: null,
        RecordId: "00000000-0000-0000-0000-000000000000",
      },
      defaultBranchCode: null,
      defaultDivisionCode: null,
      showSchedulingMethod: false,
      popUpConfig: {},
      CopyBOMVisible: false,
      TooltipRefName: "billOfMaterialsTooltip",
    };
  },
  beforeMount() {},
  async created() {
    if (this.baseView.mode === "create") {
      DefaultCompanyBranchDivisionService.getDefault().then((result) => {
        const defaultData = result.data.value[0];
        this.model.CompanyCode = defaultData.CompanyCode;
      });
      this.initialized();
    } else {
      await this.loadBOMDetails(this.baseView.id);
    }
    await this.initializeScheduleMethod();

    this.$nextTick(() => {
      this.loaded = true;

      setTimeout(() => {
        this.focusOnInitialized();
      }, 100);
    });
  },
  async mounted() {},
  beforeDestroy() {},

  computed: {
    getCopyBOMVisible() {
      return this.CopyBOMVisible;
    },
    getHeaderScreenJson() {
      return {
        headerFormVisible: true,
        headerToolbar: {
          audit: true,
          status: false,
          save: this.isUserEntryAllowed,
          toolbarFormItems: [
            {
              itemType: "group",
              caption: "",
              colCount: 2,
              items: [
                {
                  dataField: "BillNo",
                  editorType: "dxTextBox",
                  label: {
                    text: "Bill No",
                  },
                  editorOptions: {
                    tabIndex: this.baseView.mode === "create" ? 1 : -1,
                    readOnly: this.baseView.mode !== "create",
                    maxLength: 20,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    onValueChanged: this.onBillNoValueChanged,
                  },
                },
                {
                  screenField: "BillType",
                  dataField: "BomTypeCode",
                  editorOptions: {
                    tabIndex: this.isBillReadOnly ? -1 : 2,
                    readOnly: this.isBillReadOnly,
                    onFocusIn: (e) => {
                      e.event.target.select();
                    },
                    buttons: [
                      {
                        name: "info",
                        location: "after",
                        options: {
                          icon: "info",
                          onInitialized: this.DefaultBOMRestrictionsToolTip,
                          visible: this.isBillReadOnly,
                          elementAttr: {
                            class: "info-tooltip-icon",
                          },
                        },
                      },
                    ],
                  },
                },
              ],
            },
          ],
          toolbarAfterFormItems: [
            {
              screenField: "Inactive",
              dataField: "IsActive",
              editorType: "dxSwitch",
            },
          ],
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    // // disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    // disabled: true,
                  },
                },
              ],
            },
          ],
          editCallBack: this.editCallBack,
          ellipsis: {
            icon: "copy",
            menuItems: [
              {
                menuText: "Copy BOM Only",
                disabled: !this.isUserEntryAllowed,
              },
              {
                menuText: "Copy BOM & Product",
                disabled: !this.isUserEntryAllowed,
              },
            ],
            visible:
              this.baseView.mode === "review" && this.model.Status !== "X",
            clickAction: (e) => {
              if (e.itemData.menuText == "Copy BOM Only") {
                this.onClickCopyBOM();
              } else {
                this.onClickCopyBOMAndProduct();
              }
            },
          },
        },
        formItems: [
          {
            itemType: "group",
            caption: "",
            colCount: 3,
            items: [
              {
                itemType: "group",
                caption: "",
                colCount: 1,
                items: [
                  {
                    screenField: "Description",
                    dataField: "Description1",
                    label: {
                      text: "Description 1",
                    },
                    editorOptions: {
                      tabIndex: 3,
                      onFocusIn: (e) => {
                        e.event.target.select();
                      },
                      maxLength: 200,
                    },
                    required: true,
                  },
                  {
                    screenField: "Description",
                    dataField: "Description2",
                    label: {
                      text: "Description 2",
                    },
                    editorOptions: {
                      tabIndex: 4,
                      maxLength: 200,
                    },
                  },
                  {
                    screenField: "Branch",
                    label: {
                      text: "Branch",
                    },
                    dataField: "BranchCode",
                    required: true,
                    editorOptions: {
                      searchEnabled: true,
                      showClearButton: true,
                      tabIndex: 5,
                    },
                    displayExpr: (item) => {
                      if (item) return item.Code + " - " + item.Description;
                      else return "";
                    },
                  },
                  {
                    screenField: "Division",
                    label: {
                      text: "Division",
                    },
                    dataField: "DivisionCode",
                    required: true,
                    editorOptions: {
                      searchEnabled: true,
                      showClearButton: true,
                      tabIndex: 6,
                    },
                    displayExpr: (item) => {
                      if (item) return item.Code + " - " + item.Description;
                      else return "";
                    },
                  },
                ],
              },
              {
                itemType: "group",
                caption: "",
                colCount: 1,
                items: [
                  {
                    screenField: "Product",
                    dataField: "ProductCode",
                    filter: [["Source", "=", "M"]],
                    editorOptions: {
                      dropDownOptions: {
                        width: 320,
                        wrapperAttr: { class: "product-dropdown" },
                      },
                      searchEnabled: true,
                      showClearButton: true,
                      tabIndex: this.isBillReadOnly ? -1 : 7,
                      onSelectionChanged: this.onProductSelectionChanged,
                      readOnly: this.isBillReadOnly,
                      buttons: [
                        {
                          name: "info",
                          location: "after",
                          options: {
                            icon: "info",
                            onInitialized: this.DefaultBOMRestrictionsToolTip,
                            visible: this.isBillReadOnly,
                            elementAttr: {
                              class: "info-tooltip-icon",
                            },
                          },
                        },
                      ],
                    },
                    required: true,
                    displayExpr: (item) => {
                      if (item) return item.Code + " - " + item.Description1;
                      else return "";
                    },
                  },
                  {
                    dataField: "UnitOfMeasureCode",
                    editorType: "dxTextBox",
                    label: { text: "Product UM" },
                    editorOptions: {
                      tabIndex: -1,
                      disabled: true,
                      readOnly: true,
                    },
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 2,
                    items: [
                      {
                        dataField: "PackSize",
                        editorType: "dxNumberBox",
                        label: { text: "Pack Size" },
                        cssClass: "pack-size",
                        editorOptions: {
                          format: "Decimal-16-4",
                          tabIndex: 8,
                          onFocusIn: (e) => {
                            e.event.target.select();
                          },
                        },
                        validationRules: [
                          {
                            type: "range",
                            message:
                              "Pack size must be greater than or equal to 0",
                            min: 0,
                          },
                        ],
                      },
                      {
                        dataField: "BatchSize",
                        editorType: "dxNumberBox",
                        label: { text: "Batch Size" },
                        cssClass: "batch-size",
                        editorOptions: {
                          tabIndex: 9,
                          format: "Decimal-16-4",
                        },
                        validationRules: [
                          {
                            type: "range",
                            message:
                              "Batch size must be greater than or equal to 0",
                            min: 0,
                          },
                        ],
                      },
                    ],
                  },
                  {
                    dataField: "Margin",
                    editorType: "dxNumberBox",
                    label: { text: "Margin (%)" },
                    editorOptions: {
                      format: "Decimal-3-2",
                      tabIndex: 10,
                    },
                    validationRules: [
                      {
                        type: "range",
                        message: "Margin must be greater than or equal to 0",
                        min: 0,
                      },
                    ],
                  },
                ],
              },
              {
                itemType: "group",
                caption: "",
                colCount: 1,
                items: [
                  {
                    dataField: "WasteReleaseStrategy",
                    label: { text: "Waste Release Method" },
                    editorType: "dxSelectBox",
                    editorOptions: {
                      dataSource: [
                        { Name: "None", Code: "None" },
                        { Name: "Waste Separated", Code: "Waste Separated" },
                        { Name: "Waste Subtracted", Code: "Waste Subtracted" },
                      ],
                      displayExpr: "Name",
                      valueExpr: "Code",
                      searchEnabled: true,
                      showClearButton: true,
                      tabIndex: 11,
                    },
                  },
                  {
                    dataField: "BatchExpiryMethod",
                    label: { text: "Expiry Method" },
                    editorType: "dxSelectBox",
                    editorOptions: {
                      dataSource: [
                        { Name: "None", Code: "None" },
                        { Name: "Material", Code: "Material" },
                        { Name: "Released", Code: "Released" },
                        { Name: "Scheduled", Code: "Scheduled" },
                      ],
                      displayExpr: "Name",
                      valueExpr: "Code",
                      searchEnabled: true,
                      showClearButton: true,
                      tabIndex: 12,
                    },
                    visible: this.model.IsBatchProduct !== "No",
                  },
                  {
                    label: { text: "WIP Lead Days" },
                    dataField: "WipLeadDays",
                    editorType: "dxNumberBox",
                    editorOptions: {
                      tabIndex: 13,
                    },
                    validationRules: [
                      {
                        type: "range",
                        message:
                          "WIP lead days must be greater than or equal to 0",
                        min: 0,
                      },
                    ],
                  },
                  {
                    label: { text: "Work Order Per Batch" },
                    dataField: "WorkOrderPerBatch",
                    editorType: "dxSwitch",
                    editorOptions: {
                      tabIndex: 14,
                      onValueChanged: this.onWorkOrderPerBatchValueChanged,
                    },
                  },
                  {
                    itemType: "group",
                    caption: "",
                    colCount: 2,
                    items: [
                      {
                        label: { text: "Rounding Method" },
                        dataField: "WorkOrderPerBatchRoundingValue",
                        editorType: "dxSelectBox",
                        editorOptions: {
                          dataSource: [
                            {
                              Name: "None",
                              Code: "None",
                            },
                            { Name: "Half", Code: "Half" },
                            { Name: "Full", Code: "Full" },
                          ],
                          displayExpr: "Name",
                          valueExpr: "Code",
                          searchEnabled: true,
                          showClearButton: true,
                          tabIndex: 15,
                        },
                        validationRules: [
                          {
                            type: "custom",
                            validationCallback: ({ value, rule }) => {
                              rule.message = `Rounding Method is required`;
                              return (
                                !this.model.WorkOrderPerBatch ||
                                (this.model.WorkOrderPerBatch && value != null)
                              );
                            },
                            reevaluate: true,
                          },
                        ],
                        visible: false,
                      },
                      {
                        label: { text: "Rounding Direction" },
                        dataField: "WorkOrderPerBatchRoundingDirection",
                        editorType: "dxSelectBox",
                        editorOptions: {
                          dataSource: [
                            { Name: "Up", Code: "Up" },
                            { Name: "Down", Code: "Down" },
                          ],
                          displayExpr: "Name",
                          valueExpr: "Code",
                          searchEnabled: true,
                          showClearButton: true,
                          tabIndex: 16,
                          onKeyDown: (e) => {
                            if (forwardTabbing(e)) {
                              e.event.preventDefault();
                              this.focusOnFormField("Comment");
                            }
                          },
                        },
                        validationRules: [
                          {
                            type: "custom",
                            validationCallback: ({ value, rule }) => {
                              rule.message = `Rounding Direction is required`;
                              return (
                                !this.model.WorkOrderPerBatch ||
                                (this.model.WorkOrderPerBatch && value != null)
                              );
                            },
                            reevaluate: true,
                          },
                        ],
                        visible: false,
                      },
                    ],
                  },
                  {
                    label: { text: "Scheduling Method" },
                    dataField: "SchedulingMethod",
                    editorType: "dxSelectBox",
                    editorOptions: {
                      dataSource: [
                        { Name: "Continuous Flow", Code: "Continuous Flow" },
                        { Name: "Manual", Code: "Manual" },
                      ],
                      displayExpr: "Name",
                      valueExpr: "Code",
                      searchEnabled: true,
                      tabIndex: 17,
                    },
                    visible: this.showSchedulingMethod,
                  },
                ],
              },
            ],
          },
          {
            screenField: "Comments",
            dataField: "Comment",
            editorType: "dxTextArea",
            label: { text: "Comments" },
            editorOptions: {
              height: 90,
              tabIndex: 17,
              maxLength: 1000,
              onKeyDown: (e) => {
                if (backwardTabbing(e)) {
                  e.event.preventDefault();

                  const dataField = this.model.WorkOrderPerBatch
                    ? "WorkOrderPerBatchRoundingDirection"
                    : "WorkOrderPerBatch";
                  this.focusOnFormField(dataField);
                }
              },
              onFocusIn: (e) => {
                e.event.target.select();
              },
            },
          },
        ],
      };
    },
    backButtonRoute() {
      return "/bill-of-materials";
    },
    getTabs() {
      return BillOfMaterialsTabs(this.baseView);
    },
    selectedTabIndex() {
      return GetIndexByTabName("Header", this.baseView);
    },

    isBillReadOnly() {
      if (this.baseView.mode !== "create") {
        if (this.model.BomTypeCode === "Production") {
          return (
            this.model.PrimaryBomCode &&
            this.model.PrimaryBomCode === this.model.BillNo
          );
        } else if (this.model.BomTypeCode === "Planning") {
          return (
            this.model.PlanningBomCode &&
            this.model.PlanningBomCode === this.model.BillNo
          );
        } else {
          return false;
        }
      } else {
        return false;
      }
    },

    isUserEntryAllowed() {
      return FeatureStorage.checkPermission("BillOfMaterial", "create");
    },
  },
  methods: {
    DefaultBOMRestrictionsToolTip(e) {
      const self = this;
      on(e.element, "mouseover", (arg) => {
        self.$refs[this.TooltipRefName].instance.show(arg.target);
      });

      on(e.element, "mouseout", () => {
        self.$refs[this.TooltipRefName].instance.hide();
      });
    },

    onClickCopyBOM() {
      this.popUpConfig = {
        BillNo: this.model.BillNo,
        BillType: this.model.BomTypeCode,
        Description1: this.model.Description1,
        Description2: this.model.Description2,
      };

      this.setCopyBOMVisible(true);
    },
    onClickCopyBOMAndProduct() {
      this.popUpConfig = {
        BillNo: this.model.BillNo,
        BillType: this.model.BomTypeCode,
        Description1: this.model.Description1,
        Description2: this.model.Description2,
        CopyType: "BOMAndProduct",
        ProductCode: this.model.ProductCode,
      };

      this.setCopyBOMVisible(true);
    },
    setCopyBOMVisible(val) {
      this.CopyBOMVisible = val;

      if (!val) {
        this.popUpConfig = {};
      }
    },
    async initialized() {
      await this.$store.dispatch("screenFactors/getFactors", [65, 66]);
      this.defaultBranchCode = getFactorByCode(65).Factorc;
      this.defaultDivisionCode = getFactorByCode(66).Factorc;

      if (this.defaultBranchCode) {
        this.model.BranchCode = this.defaultBranchCode;
      }
      if (this.defaultDivisionCode) {
        this.model.DivisionCode = this.defaultDivisionCode;
      }
    },
    async initializeScheduleMethod(){
      await this.$store.dispatch("screenFactors/getFactors", [79]);
      this.showSchedulingMethod = getFactorByCode(79).Factorc === "Continuous Flow";
    },
    async loadBOMDetails(bomCode) {
      this.showLoading(true);
      try {
        const response = await BillOfMaterialsService.getByCode(bomCode);
        if (IsResponseDataValid(response)) {
          const data = response.data.value[0];

          Object.assign(this.model, {
            BillNo: data.Code,
            BomTypeCode: data.BomTypeCode,
            IsActive: data.Status === "A" ? true : false,
            Description1: data.Description1,
            Description2: data.Description2,
            BranchCode: data.BranchCode,
            DivisionCode: data.DivisionCode,
            ProductCode: data.ProductCode,
            PackSize: data.PackSize,
            UnitOfMeasureCode: data.UnitOfMeasureCode,
            BatchSize: data.BatchSize,
            Margin: data.Margin,
            WasteReleaseStrategy: data.WasteReleaseStrategy,
            BatchExpiryMethod: data.BatchExpiryMethod,
            WipLeadDays: data.WipLeadDays,
            WorkOrderPerBatch: data.WorkOrderPerBatch === "Y" ? true : false,
            WorkOrderPerBatchRoundingValue: data.WorkOrderPerBatchRoundingValue,
            WorkOrderPerBatchRoundingDirection:
              data.WorkOrderPerBatchRoundingDirection,
            SchedulingMethod: data.SchedulingMethod,
            Comment: data.Comment,
            DateCreated: data.DateCreated,
            DateChanged: data.DateChanged,
            CreatedBy: data.CreatedBy,
            ChangedBy: data.ChangedBy,
            CompanyCode: data.CompanyCode,
            RecordId: data.RecordId,
          });

          this.getProductDetails(this.model.ProductCode);
        }
      } catch (error) {
        this.displayServerError(error, "Error loading Bill of Materials");
      } finally {
        this.updateRoundingMethodAndDirectionVisibility();
      }
    },

    async getProductDetails(productCode) {
      try {
        const response = await ProductService.getByCode(productCode, {
          select: "Code,IsBatchProduct, PrimaryBomCode, PlanningBomCode",
        });
        if (IsResponseDataValid(response)) {
          this.model = {
            ...this.model,
            IsBatchProduct: response.data.value[0].IsBatchProduct,
            PrimaryBomCode: response.data.value[0].PrimaryBomCode,
            PlanningBomCode: response.data.value[0].PlanningBomCode,
          };
        }
        this.showLoading(false);
      } catch (error) {
        this.displayServerError(error, "Error loading Product details");

        this.showLoading(false);
      } finally {
        this.updateRoundingMethodAndDirectionVisibility();
      }
    },
    onWorkOrderPerBatchValueChanged(e) {
      this.model.WorkOrderPerBatch = e.value;

      if (!e.event) return;

      //
      this.model.WorkOrderPerBatchRoundingValue = e.value ? "Full" : "None";
      this.model.WorkOrderPerBatchRoundingDirection = "Up";
      //
      this.$nextTick(() => {
        setTimeout(() => {
          this.validateForm();
          this.focusOnFormField("WorkOrderPerBatchRoundingValue");

          this.$nextTick(() => {
            this.updateRoundingMethodAndDirectionVisibility();
          });
        }, 150);
      });
    },
    onProductSelectionChanged(e) {
      if (e.selectedItem) {
        this.model.IsBatchProduct = e.selectedItem.IsBatchProduct;
        this.model.ProductCode = e.selectedItem.Code;
        this.model.UnitOfMeasureCode = e.selectedItem.UnitOfMeasureCode;
      } else {
        this.model.IsBatchProduct = "No";
        this.model.ProductCode = null;
        this.model.UnitOfMeasureCode = null;
      }

      this.$nextTick(() => {
        setTimeout(() => {
          this.focusOnFormField("PackSize");
        }, 100);
      });
    },
    async saveTransaction(isValid, brokenRules) {
      const activeElement = document.activeElement;
      if (activeElement) {
        activeElement.blur();
      }

      setTimeout(async () => {
        if (!isValid) this.displayValidationSummary(brokenRules);
        else {
          this.saveBOMHeader();
        }
      }, 100);
    },
    backButtonFunction() {
      this.$router
        .push({
          path: this.backButtonRoute,
        })
        .then(() => {
          this.$store.dispatch("dataContext/setDataViewType", undefined);
        });
    },

    focusOnInitialized() {
      setTimeout(() => {
        this.updateRoundingMethodAndDirectionVisibility();
      }, 100);
      if (this.baseView.mode === "review") return;

      let dataField = "BillNo";
      const headerToolBarForm =
        this.$refs["gc-header-form-component"].$refs["gc-form-header-toolbar"]
          .$refs["toolbar-form"].$refs["gc-form-comp"].instance;

      if (this.baseView.mode !== "create") {
        if (this.isBillReadOnly) dataField = "Description1";
        else dataField = "BomTypeCode";
      }

      if (
        this.baseView.mode === "create" ||
        (this.baseView.mode !== "create" && !this.isBillReadOnly)
      ) {
        if (headerToolBarForm) {
          headerToolBarForm.getEditor(dataField).instance().focus();
        }
      } else {
        if (this.baseView.mode === "edit") {
          this.focusOnFormField(dataField);
        }
      }

      this.showLoading(false);
    },

    updateRoundingMethodAndDirectionVisibility() {
      this.$nextTick(() => {
        this.changeFormItemOption(
          "WorkOrderPerBatchRoundingValue",
          "visible",
          this.model.WorkOrderPerBatch
        );
        this.changeFormItemOption(
          "WorkOrderPerBatchRoundingDirection",
          "visible",
          this.model.WorkOrderPerBatch
        );
      });
    },

    focusOnFormField(dataField) {
      const formInstance =
        this.$refs["gc-header-form-component"].$refs["gc-header-form"].$refs[
          "gc-form-comp"
        ].instance;
      if (formInstance && formInstance.getEditor(dataField)) {
        formInstance.getEditor(dataField).focus();
      }
    },

    changeFormItemOption(dataField, optionName, optionValue) {
      const formInstance =
        this.$refs["gc-header-form-component"].$refs["gc-header-form"].$refs[
          "gc-form-comp"
        ].instance;

      if (!formInstance) return;

      // NOTE: use itemOption, not getEditor
      formInstance.itemOption(dataField, optionName, optionValue);
    },
    validateForm() {
      const formInstance =
        this.$refs["gc-header-form-component"].$refs["gc-header-form"].$refs[
          "gc-form-comp"
        ].instance;
      if (formInstance) {
        formInstance.validate();
      }
    },

    editCallBack() {
      this.updateRoundingMethodAndDirectionVisibility();
      this.$nextTick(() => {
        setTimeout(() => {
          this.focusOnInitialized();
        }, 500);
      });
    },

    async saveBOMHeader() {
      this.showLoading(true);
      try {
        const response = await BillOfMaterialsService.saveBOM(
          this.getPayload()
        );

        this.setIgnoreRouteChangeConfirmation();
        if (response && response.data) {
          if (response.data.Code) {
            this.model.BillNo = response.data.Code;
            this.$router
              .push({
                path: "/bill-of-materials/review/" + response.data.Code,
              })
              .then(() => {
                this.loadBOMDetails(this.model.BillNo);
              });
          }
        }
      } catch (error) {
        this.displayServerError(error, "Error saving Bill of Materials");
      } finally {
        this.showLoading(false);
      }
    },

    getPayload() {
      const payload = {
        Code: this.model.BillNo,
        BomTypeCode: this.model.BomTypeCode,
        Status: this.model.IsActive ? "A" : "X",
        Description1: this.model.Description1,
        Description2: this.model.Description2,
        BranchCode: this.model.BranchCode,
        DivisionCode: this.model.DivisionCode,
        ProductCode: this.model.ProductCode,
        PackSize: this.model.PackSize,
        BatchSize: this.model.BatchSize,
        Margin: this.model.Margin,
        WasteReleaseStrategy: this.model.WasteReleaseStrategy,
        WipLeadDays: this.model.WipLeadDays,
        WorkOrderPerBatch: this.model.WorkOrderPerBatch ? "Y" : "N",
        WorkOrderPerBatchRoundingDirection:
          this.model.WorkOrderPerBatchRoundingDirection,
        SchedulingMethod: this.model.SchedulingMethod,
        WorkOrderPerBatchRoundingValue:
          this.model.WorkOrderPerBatchRoundingValue,
        BatchExpiryMethod: this.model.BatchExpiryMethod,
        Comment: this.model.Comment,
        CompanyCode: this.model.CompanyCode,
        RecordId: this.model.RecordId,
        // DivisionCode: "50",
        // Conversion: 0,
        // FrCode: "",
        // Locked: false,
        // BomTemplateCode: 0,
      };

      return payload;
    },

    async onBillNoValueChanged(e) {
      if (this.baseView.mode !== "review" && e.event) {
        try {
          const response = await BillOfMaterialsService.getByCode(
            e.value,
            "Code,RecordId"
          );
          if (IsResponseDataValid(response)) {
            await this.$alert(
              "Bill of Materials with this Bill No already exists.",
              "Validation Error"
            );
            this.model.BillNo = null;
            this.$nextTick(() => {
              setTimeout(() => {
                this.focusOnInitialized();
              }, 100);
            });
          }
        } catch (error) {
          this.displayServerError(error, "Error validating Bill of Materials");
        }
      }
    },
  },
};
</script>

<style lang="scss">
.bill-of-materials {
  .view-toolbar .dx-form .dx-item-content .dx-item.dx-box-item {
    width: 600px !important;
  }
  .pack-size {
    padding-right: 0 !important;
  }
  .batch-size {
    padding-left: 10 !important;
    > label {
      // width: 75px !important;
    }
  }

  .info-tooltip-icon {
    .dx-button-content .dx-icon::before {
      color: #ff9800 !important;
    }

    .dx-icon {
      cursor: pointer;
    }
  }
}

.product-dropdown {
  .custom-auto-complete {
    text-wrap: auto !important;
  }
}
</style>
